/**
 * Feminapause Quiz
 * Asset: assets/feminapause-quiz.js
 * v4 — Name field added, all steps required
 *
 * CHANGES FROM v3:
 *   - First name field added at final step alongside email
 *   - All steps now required before Continue is enabled
 *   - Brands tried remains optional (free text)
 *   - FNAME now sent to Mailchimp for personalised emails
 */

(function () {
  'use strict';

  // ─── MAILCHIMP CONFIG ───────────────────────────────────────────────────────
  const MAILCHIMP_DC      = 'us10';
  const MAILCHIMP_USER_ID = '8fc3db13d7f62e73fa93c4453';
  const MAILCHIMP_LIST_ID = 'a87e682d2f';

  // ─── STEPS ─────────────────────────────────────────────────────────────────
  const STEPS = [
    {
      id: 'symptoms',
      type: 'multi',
      label: 'Step 1 of 9',
      question: 'Which of these have you been experiencing recently?',
      hint: 'Select all that apply',
      required: true,
      options: [
        'Trouble falling asleep',
        'Waking during the night (2–4am)',
        'Night sweats / overheating',
        'Feeling constantly tired',
        'Brain fog / forgetfulness',
        'Anxiety or feeling on edge',
        'Low mood / irritability',
        'Hot flushes during the day',
        'Hormonal fluctuations / irregular cycles',
        'Low libido',
        'Joint aches or inflammation',
        'None of these / not sure',
      ],
      allowOther: true,
    },
    {
      id: 'primary_goal',
      type: 'single',
      label: 'Step 2 of 9',
      question: "What's the one thing you'd most like to improve right now?",
      required: true,
      options: [
        'Sleep — I just want a full night\'s rest',
        'My mood — I don\'t feel like myself',
        'My energy — I feel constantly drained',
        'My focus — I can\'t think clearly',
        'A bit of everything',
      ],
    },
    {
      id: 'duration',
      type: 'single',
      label: 'Step 3 of 9',
      question: 'How long have you been dealing with this?',
      required: true,
      options: [
        'Just started (last few weeks)',
        'A few months',
        'Over 6 months',
        'Over a year',
      ],
    },
    {
      id: 'severity',
      type: 'single',
      label: 'Step 4 of 9',
      question: 'How much is this affecting your daily life?',
      required: true,
      options: [
        'Slightly annoying',
        'Noticeable, but manageable',
        'Affecting my day-to-day life',
        'Completely draining',
      ],
    },
    {
      id: 'tried',
      type: 'multi',
      label: 'Step 5 of 9',
      question: 'Have you tried anything to help so far?',
      hint: 'Select all that apply',
      required: true,
      options: [
        'Supplements',
        'HRT',
        'Sleep aids',
        'Herbal remedies',
        'Nothing yet',
        'Other',
      ],
    },
    {
      id: 'brands_tried',
      type: 'text',
      label: 'Step 5b of 9',
      question: 'If you remember any brands or products you\'ve tried, feel free to share them',
      hint: 'Optional — helps us understand what hasn\'t worked for you',
      placeholder: 'e.g. Menopace, Wellwoman, Boots own brand…',
      required: false, // ← intentionally optional
      showIf: (a) => {
        const t = a.tried || [];
        return t.length > 0 && !(t.length === 1 && t[0] === 'Nothing yet');
      },
    },
    {
      id: 'effectiveness',
      type: 'single',
      label: 'Step 5c of 9',
      question: 'Did anything you tried actually help?',
      required: true,
      options: [
        'Yes, but not enough',
        'No, nothing worked',
        'It worked for a while, then stopped',
        'Not sure',
      ],
      showIf: (a) => {
        const t = a.tried || [];
        return t.length > 0 && !(t.length === 1 && t[0] === 'Nothing yet');
      },
    },
    {
      id: 'buying_stage',
      type: 'single',
      label: 'Step 6 of 9',
      question: 'Where are you right now in your journey?',
      required: true,
      options: [
        'Just starting to look into solutions',
        'Actively looking for something that works',
        'Tried things before, nothing has worked',
        'Ready to find something that actually helps',
      ],
    },
    {
      id: 'value',
      type: 'single',
      label: 'Step 7 of 9',
      question: 'What matters most to you?',
      required: true,
      options: [
        'Quick relief',
        'Long-term support',
        'A balance of both',
      ],
    },
    {
      id: 'outcome',
      type: 'single',
      label: 'Step 8 of 9',
      question: 'If this could change one thing for you, what would it be?',
      required: true,
      options: [
        'I\'d finally feel like myself again',
        'I\'d have more energy for my day',
        'I\'d sleep properly',
        'I\'d feel calmer and more in control',
      ],
    },
    {
      id: 'contact',
      type: 'contact',
      label: 'Step 9 of 9',
      question: 'Where should we send your personalised results?',
      hint: 'We\'ll personalise your results and send tailored support — no spam, ever.',
      required: true,
    },
  ];

  // ─── STATE ──────────────────────────────────────────────────────────────────
  let currentIndex = 0;
  const answers = {};
  let submitting = false;

  // ─── HELPERS ────────────────────────────────────────────────────────────────
  function visibleSteps() {
    return STEPS.filter(s => !s.showIf || s.showIf(answers));
  }

  function currentStep() {
    return visibleSteps()[currentIndex];
  }

  function stripGoalLabel(g) {
    if (!g) return '';
    return (g.split('—')[0] || g.split('–')[0] || g).trim();
  }

  // ─── VALIDATION ─────────────────────────────────────────────────────────────
  function isStepComplete(step) {
    if (!step.required) return true; // optional steps always pass

    switch (step.type) {
      case 'single':
        return !!answers[step.id];

      case 'multi':
        return Array.isArray(answers[step.id]) && answers[step.id].length > 0;

      case 'text':
        // only validate if required (brands_tried is not required)
        return !step.required || ((answers[step.id] || '').trim().length > 0);

      case 'contact':
        const name  = (answers.first_name || '').trim();
        const email = (answers.email || '').trim();
        const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        return name.length > 0 && emailValid;

      default:
        return true;
    }
  }

  function getValidationMessage(step) {
    switch (step.type) {
      case 'single':
        return 'Please select an option to continue.';
      case 'multi':
        return 'Please select at least one option to continue.';
      case 'contact':
        const name  = (answers.first_name || '').trim();
        const email = (answers.email || '').trim();
        if (!name) return 'Please enter your first name.';
        if (!email) return 'Please enter your email address.';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Please enter a valid email address.';
        return '';
      default:
        return 'Please complete this step to continue.';
    }
  }

  // ─── BUILD TAGS ─────────────────────────────────────────────────────────────
  function buildTags() {
    const tags = ['Quiz Lead'];
    const goal = (answers.primary_goal || '').toLowerCase();
    if (goal.includes('sleep'))      tags.push('Quiz: Sleep');
    else if (goal.includes('mood'))  tags.push('Quiz: Mood');
    else if (goal.includes('energy'))tags.push('Quiz: Energy');
    else if (goal.includes('focus')) tags.push('Quiz: Focus');
    else if (goal.includes('everything')) tags.push('Quiz: Everything');
    const tried = answers.tried || [];
    if (tried.length > 0 && !(tried.length === 1 && tried[0] === 'Nothing yet')) {
      tags.push('Quiz: Tried Before');
    }
    if ((answers.buying_stage || '').includes('Ready')) tags.push('Quiz: High Intent');
    if ((answers.brands_tried || '').trim().length > 0) tags.push('Quiz: Named Brands');
    return tags.join(',');
  }

  // ─── MAILCHIMP SUBMIT ───────────────────────────────────────────────────────
  function pushToMailchimp(email, firstName) {
    const baseUrl = `https://${MAILCHIMP_DC}.list-manage.com/subscribe/post-json`;
    const params = new URLSearchParams({
      u:         MAILCHIMP_USER_ID,
      id:        MAILCHIMP_LIST_ID,
      EMAIL:     email,
      FNAME:     firstName,           // ← first name now sent to Mailchimp
      GOAL:      stripGoalLabel(answers.primary_goal),
      SYMPTOMS:  (answers.symptoms || []).join(', '),
      DURATION:  answers.duration || '',
      SEVERITY:  answers.severity || '',
      TRIED:     (answers.tried || []).join(', '),
      BRANDS:    answers.brands_tried || '',
      EFFECTIVE: answers.effectiveness || '',
      BUYSTAGE:  answers.buying_stage || '',
      VALUE:     answers.value || '',
      OUTCOME:   answers.outcome || '',
      tags:      buildTags(),
      c:         'fpMailchimpCallback',
    });

    window.fpMailchimpCallback = function (data) {
      if (data.result === 'success') {
        console.log('[Feminapause Quiz] Mailchimp: subscribed successfully');
      } else {
        console.log('[Feminapause Quiz] Mailchimp:', data.msg);
      }
      const script = document.getElementById('fp-mc-script');
      if (script) script.remove();
      delete window.fpMailchimpCallback;
    };

    const script = document.createElement('script');
    script.id  = 'fp-mc-script';
    script.src = `${baseUrl}?${params.toString()}`;
    document.body.appendChild(script);
  }

  // ─── RESULTS COPY ───────────────────────────────────────────────────────────
  function resultsContent() {
    const goal = answers.primary_goal || '';
    const symptoms = answers.symptoms || [];
    const firstName = (answers.first_name || '').trim();
    let insight = 'a combination of symptoms linked to hormonal changes';
    let cta_focus = 'calm, sleep and balance';
    if (goal.includes('Sleep')) {
      insight = 'your sleep is being disrupted by hormonal shifts, especially night waking and restlessness';
      cta_focus = 'deep, restorative sleep';
    } else if (goal.toLowerCase().includes('mood')) {
      insight = 'your mood and sense of self are being affected by hormonal fluctuations';
      cta_focus = 'mood, calm and balance';
    } else if (goal.toLowerCase().includes('energy')) {
      insight = 'your energy is being depleted by the hormonal changes you\'re experiencing';
      cta_focus = 'sustained energy and vitality';
    } else if (goal.toLowerCase().includes('focus')) {
      insight = 'your focus and mental clarity are being affected by hormonal shifts';
      cta_focus = 'focus and mental clarity';
    }
    const triedBefore = (answers.tried || []).length > 0 &&
      !((answers.tried || []).includes('Nothing yet') && (answers.tried || []).length === 1);
    const validation = triedBefore
      ? 'Many women find standard supplements or HRT don\'t fully address the root cause. You\'re not alone — and there is a better way.'
      : 'This is one of the most common experiences women share with us at this stage. You\'re absolutely not alone in this.';
    return { insight, validation, cta_focus, symptoms: symptoms.slice(0, 4), firstName };
  }

  // ─── RENDER STEP ────────────────────────────────────────────────────────────
  function render() {
    const root = document.getElementById('fp-quiz-root');
    if (!root) return;
    const steps = visibleSteps();
    const step = steps[currentIndex];
    const total = steps.length;
    const pct = Math.round(((currentIndex + 1) / total) * 100);
    const current = answers[step.id];

    let bodyHTML = '';

    if (step.type === 'multi' || step.type === 'single') {
      bodyHTML += '<div class="fp-options fp-options--' + step.type + '">';
      step.options.forEach(opt => {
        const sel = step.type === 'single'
          ? current === opt
          : (Array.isArray(current) && current.includes(opt));
        bodyHTML += `<button
          class="fp-opt${sel ? ' fp-opt--selected' : ''}"
          data-value="${opt.replace(/"/g, '&quot;')}"
          data-step="${step.id}"
          data-type="${step.type}"
          type="button"
        >${opt}</button>`;
      });
      if (step.allowOther) {
        bodyHTML += `<div class="fp-other-wrap">
          <input type="text" class="fp-other-input" id="fp-other"
            placeholder="Other (please specify)"
            value="${answers[step.id + '_other'] || ''}" />
        </div>`;
      }
      bodyHTML += '</div>';
      if (step.type === 'multi') {
        bodyHTML += '<p class="fp-step__sub">Select all that apply, then tap Continue</p>';
      }

    } else if (step.type === 'text') {
      bodyHTML += `<textarea class="fp-textarea" id="fp-text-input"
        placeholder="${step.placeholder || ''}" rows="3">${current || ''}</textarea>`;
      if (!step.required) {
        bodyHTML += '<p class="fp-step__sub">Optional — skip if you prefer</p>';
      }

    } else if (step.type === 'contact') {
      // Name + Email on the same step
      bodyHTML += `
        <input
          type="text"
          class="fp-email-input"
          id="fp-name-input"
          placeholder="Your first name"
          value="${answers.first_name || ''}"
          autocomplete="given-name"
          style="margin-bottom: 0.75rem;"
        />
        <input
          type="email"
          class="fp-email-input"
          id="fp-email-input"
          placeholder="your@email.com"
          value="${answers.email || ''}"
          autocomplete="email"
        />
        <p class="fp-consent">By continuing, you agree to receive personalised emails from Feminapause. Unsubscribe any time.</p>
      `;
    }

    const isLast = currentIndex === total - 1;
    const backBtn = currentIndex > 0
      ? `<button class="fp-btn fp-btn--back" id="fp-back" type="button">← Back</button>` : '';

    // Required badge on steps that need an answer
    const requiredBadge = step.required
      ? `<span style="color: var(--fp-salmon); font-size: 12px; margin-left: 6px;">*&nbsp;Required</span>`
      : '';

    root.innerHTML = `
      <div class="fp-quiz">
        <div class="fp-header">
          <p class="fp-header__logo">Feminapause</p>
          <h1 class="fp-header__title">Find your personalised support plan</h1>
          <p class="fp-header__sub">Trusted by thousands of women</p>
        </div>
        <div class="fp-progress-wrap">
          <div class="fp-progress-top">
            <span class="fp-progress-label">${step.label}</span>
            <span class="fp-progress-pct">${pct}%</span>
          </div>
          <div class="fp-progress"><div class="fp-progress__bar" style="width:${pct}%"></div></div>
        </div>
        <div class="fp-card">
          <h2 class="fp-step__question">${step.question}${requiredBadge}</h2>
          ${step.hint ? `<p class="fp-step__hint">${step.hint}</p>` : ''}
          ${bodyHTML}
          <div class="fp-nav">
            ${backBtn}
            <button class="fp-btn fp-btn--next" id="fp-next" type="button">
              ${isLast ? 'See my results →' : 'Continue →'}
            </button>
          </div>
        </div>
        <div class="fp-trust">
          <span>🔒 Private & secure</span>
          <span class="fp-trust-dot"></span>
          <span>No spam, ever</span>
          <span class="fp-trust-dot"></span>
          <span>Unsubscribe any time</span>
        </div>
      </div>
    `;

    attachListeners(step);
  }

  // ─── RENDER RESULTS ─────────────────────────────────────────────────────────
  function renderResults() {
    const root = document.getElementById('fp-quiz-root');
    if (!root) return;
    const { insight, validation, cta_focus, symptoms, firstName } = resultsContent();
    const email = answers.email || '';
    const greeting = firstName ? `${firstName}, your` : 'Your';
    const pillsHTML = symptoms.length
      ? `<div class="fp-symptom-pills">${symptoms.map(s => `<span class="fp-pill">${s}</span>`).join('')}</div>` : '';

    root.innerHTML = `
      <div class="fp-quiz fp-quiz--results">
        <div class="fp-progress-wrap">
          <div class="fp-progress-top">
            <span class="fp-progress-label">Complete</span>
            <span class="fp-progress-pct">100%</span>
          </div>
          <div class="fp-progress"><div class="fp-progress__bar" style="width:100%"></div></div>
        </div>
        <div class="fp-card">
          <div class="fp-results-header">
            <div class="fp-results-header__icon">💗</div>
            <p class="fp-results-header__title">${greeting} personalised results are ready</p>
          </div>
          <span class="fp-results__tag">Your insight</span>
          <p class="fp-results__title">It sounds like ${insight}.</p>
          ${pillsHTML}
          <p class="fp-results__body">${validation}</p>
          <div class="fp-results__divider"></div>
          <span class="fp-results__tag">Our recommendation</span>
          <p class="fp-results__title">Feminapause is designed to support ${cta_focus}.</p>
          <p class="fp-results__body">Formulated by women, for women — with CBD, B12 and B6 to support you through every stage.</p>
          ${email ? `<p class="fp-results__email-note">✉ Your personalised guide is on its way to <strong>${email}</strong></p>` : ''}
          <a href="/collections/shop" class="fp-cta">Start your routine →</a>
        </div>
        <div class="fp-trust">
          <span>🔒 Private & secure</span>
          <span class="fp-trust-dot"></span>
          <span>Trusted by thousands of women</span>
        </div>
      </div>
    `;
  }

  // ─── LISTENERS ──────────────────────────────────────────────────────────────
  function attachListeners(step) {
    document.querySelectorAll('.fp-opt').forEach(btn => {
      btn.addEventListener('click', () => {
        const val = btn.dataset.value;
        const type = btn.dataset.type;
        const id = btn.dataset.step;
        if (type === 'single') {
          answers[id] = val;
          render();
          setTimeout(advance, 260);
        } else {
          if (!answers[id]) answers[id] = [];
          const i = answers[id].indexOf(val);
          if (i > -1) answers[id].splice(i, 1); else answers[id].push(val);
          render();
        }
      });
    });

    const other = document.getElementById('fp-other');
    if (other) other.addEventListener('input', e => { answers[step.id + '_other'] = e.target.value; });

    const txt = document.getElementById('fp-text-input');
    if (txt) txt.addEventListener('input', e => { answers[step.id] = e.target.value; });

    const nameInput = document.getElementById('fp-name-input');
    if (nameInput) nameInput.addEventListener('input', e => { answers.first_name = e.target.value; });

    const em = document.getElementById('fp-email-input');
    if (em) {
      em.addEventListener('input', e => { answers.email = e.target.value; });
      em.addEventListener('keydown', e => { if (e.key === 'Enter') advance(); });
    }

    const next = document.getElementById('fp-next');
    if (next) next.addEventListener('click', advance);

    const back = document.getElementById('fp-back');
    if (back) back.addEventListener('click', goBack);
  }

  // ─── NAVIGATION ─────────────────────────────────────────────────────────────
  function advance() {
    if (submitting) return;
    const step = currentStep();

    // Validate current step before advancing
    if (!isStepComplete(step)) {
      showError(getValidationMessage(step));
      return;
    }

    // Clear any existing error
    const existing = document.getElementById('fp-error');
    if (existing) existing.remove();

    const steps = visibleSteps();
    if (currentIndex < steps.length - 1) {
      currentIndex++;
      render();
      scrollTop();
    } else {
      submitting = true;
      pushToMailchimp(
        (answers.email || '').trim(),
        (answers.first_name || '').trim()
      );
      renderResults();
      scrollTop();
    }
  }

  function goBack() {
    if (currentIndex > 0) { currentIndex--; render(); scrollTop(); }
  }

  function scrollTop() {
    const root = document.getElementById('fp-quiz-root');
    if (root) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function showError(msg) {
    const existing = document.getElementById('fp-error');
    if (existing) { existing.textContent = msg; return; }
    const nav = document.querySelector('.fp-nav');
    if (nav) {
      const err = document.createElement('p');
      err.id = 'fp-error';
      err.className = 'fp-error';
      err.textContent = msg;
      nav.insertAdjacentElement('beforebegin', err);
    }
  }

  // ─── INIT ────────────────────────────────────────────────────────────────────
  function init() {
    if (!document.getElementById('fp-quiz-root')) return;
    render();
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();